import * as t from "io-ts";
import { DynamicFieldsV } from "@/types/dynamic-data";
import {
  BuilderElementV,
  BuilderElementCategoriesV,
  RequiredPluginV,
  ElementsExternalAttrsDepsAndActionsV,
  MacrosDataV
} from "@/types/elements";
import { PropertiesDataV } from "@/types/tree";
import { FontsV } from "@/types/fonts";
import { GlobalSettingsControlsAndTemplateV } from "./global-and-document-settings";
import { BreakdancePluginsV } from "./plugins";
import { TemplatePreviewV } from "./templates";
import { LoadedSelectorDataV } from "./selectors";
import {
  BreakdanceNeededDataV,
  BreakdanceDocumentSpecificNeededUrlsV
} from "@/types/config";
import {
  DocumentMetasV,
  BreakdanceDocumentWithExportedTreeV
} from "@/types/document";
import { ConditionsV } from "./conditions";
import { PreferencesV } from "./preferences";
import { ElementPresetsV } from "./presets";
import { BreakpointV } from "./breakpoints";
import { PermissionV } from "@/types/permission";
import { GlobalMediaSizeV } from "@/types/media";
import { DesignLibraryDataV } from "@/types/design-library";
import { getIoTsStringsUnionFromConstStringArray } from "@/util/type-assertions";
import { ControlV } from "@/types/controls";

import { LoadedVariablesDataV } from "@/oxygen/variables/lib/types";

import { LoadedOxySelectorDataV } from "@/oxygen/classes/lib/types";

const InvalidPostsErrorV = t.union([
  t.literal("post_does_not_exist"),
  t.literal("post_type_is_banned"),
  t.literal("post_type_is_restricted"),
  t.literal("post_is_in_trash")
]);

const InvalidPostDataV = t.type({
  errorType: InvalidPostsErrorV,
  postType: t.union([t.undefined, t.string]),
  backToAdminUrl: t.string
});

export type InvalidPostData = t.TypeOf<typeof InvalidPostDataV>;

export const subscriptionMode = ["free", "pro"] as const;

export const SubscriptionModeV =
  getIoTsStringsUnionFromConstStringArray(subscriptionMode);

export type SubscriptionMode = t.TypeOf<typeof SubscriptionModeV>;

export const AiV = t.type({
  enabled: t.boolean
});
export type Ai = t.TypeOf<typeof AiV>;

export const InteractionActionV = t.type({
  name: t.string,
  slug: t.string,
  jsFunctionName: t.string,
  controls: t.array(ControlV)
});

export const InteractionTriggerV = t.type({
  name: t.string,
  slug: t.string,
  controls: t.array(ControlV),
  jsFunctionName: t.string
});
export type InteractionTrigger = t.TypeOf<typeof InteractionTriggerV>;

export const InteractionsV = t.type({
  actions: t.array(InteractionActionV),
  triggers: t.array(InteractionTriggerV)
});
export type Interactions = t.TypeOf<typeof InteractionsV>;

export const EnvironmentV = t.union([
  t.literal("local"),
  t.literal("production")
]);

export type Environment = t.TypeOf<typeof EnvironmentV>;
const ConfigDataLoadedFromWordPressV = t.type({
  environment: EnvironmentV,
  builderMode: t.union([t.literal("breakdance"), t.literal("oxygen")]),
  elementPresets: ElementPresetsV,
  neededData: BreakdanceNeededDataV,
  builderElements: t.array(BuilderElementV),
  elementsExternalAttrsDepsAndActions: ElementsExternalAttrsDepsAndActionsV,
  builderElementCategories: BuilderElementCategoriesV,
  preferences: PreferencesV,
  dynamicFields: DynamicFieldsV,
  conditions: ConditionsV,
  fonts: FontsV,
  globalSettingsControlsAndTemplate: GlobalSettingsControlsAndTemplateV,
  globalSettings: PropertiesDataV,
  plugins: BreakdancePluginsV,
  availableDocuments: DocumentMetasV,
  selectors: LoadedSelectorDataV,
  availableMediaSizes: t.array(GlobalMediaSizeV),
  availablePermissions: t.array(PermissionV),
  availableWordpressPlugins: t.array(RequiredPluginV),
  permission: PermissionV,
  builtinBreakpoints: t.array(BreakpointV),
  twigMacros: MacrosDataV,
  twigMacrosHash: t.string,
  designLibrary: DesignLibraryDataV,
  subscriptionMode: SubscriptionModeV,
  variables: LoadedVariablesDataV,
  oxySelectors: LoadedOxySelectorDataV,
  experiments: t.array(t.string),
  ai: AiV,
  interactions: InteractionsV
});

export type ConfigDataLoadedFromWordPress = t.TypeOf<
  typeof ConfigDataLoadedFromWordPressV
>;

const DocumentDataLoadedFromWordPressV = t.intersection([
  t.type({
    document: BreakdanceDocumentWithExportedTreeV,
    documentSpecificNeededUrls: BreakdanceDocumentSpecificNeededUrlsV,
    templatePreview: TemplatePreviewV
  }),
  ConfigDataLoadedFromWordPressV
]);

export type DocumentDataLoadedFromWordPress = t.TypeOf<
  typeof DocumentDataLoadedFromWordPressV
>;

export const ExpectedDataToLoadDocumentV = t.union([
  InvalidPostDataV,
  DocumentDataLoadedFromWordPressV
]);

export type ExpectedDataToLoadDocument = t.TypeOf<
  typeof ExpectedDataToLoadDocumentV
>;

export const ExpectedDataToLoadConfigV = ConfigDataLoadedFromWordPressV;

export type ExpectedDataToLoadConfig = t.TypeOf<
  typeof ExpectedDataToLoadConfigV
>;
